import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import LoginPage from './pages/LoginPage';
import KidSelectPage from './pages/KidSelectPage';
import TargetLogPage from './pages/TargetLogPage';
import PointsSummaryPage from './pages/PointsSummaryPage';
import ParentManagementPage from './pages/ParentManagementPage';
import ManageGiftsPage from './pages/ManageGiftsPage';
import LevelsPage from './pages/LevelsPage';
import StatisticsPage from './pages/StatisticsPage';
import FamilyManagementPage from './pages/FamilyManagementPage';
import JoinFamilyPage from './pages/JoinFamilyPage';
import KidDashboardPage from './pages/KidDashboardPage';
import FriendsPage from './pages/FriendsPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import EnergyPage from './pages/EnergyPage';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { UserProvider } from './context/UserContext';
import { LevelProvider } from './context/LevelContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366f1',
      light: '#818cf8',
      dark: '#4f46e5',
    },
    secondary: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    success: {
      main: '#10b981',
      light: '#34d399',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid #e2e8f0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Box sx={{ mx: 'auto', px: 2, py: 3 }}>
          <Router>
            <AuthProvider>
              <LevelProvider>
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/join-family/:inviteCode" element={<JoinFamilyPage />} />
                  <Route path="/invite/:code" element={<AcceptInvitationPage />} />
                  <Route path="/select-kid" element={
                    <ProtectedRoute requireParent>
                      <KidSelectPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/manage" element={
                    <ProtectedRoute requireParent>
                      <ParentManagementPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/family" element={
                    <ProtectedRoute requireParent>
                      <FamilyManagementPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  <Route path="/user/:userId" element={<Navigate to="targets" replace />} />
                  <Route path="/user/:userId/*" element={
                    <ProtectedRoute>
                      <UserProvider>
                        <Navigation />
                        <Routes>
                          <Route path="targets" element={<TargetLogPage />} />
                          <Route path="points" element={<PointsSummaryPage />} />
                          <Route path="manage" element={<ParentManagementPage />} />
                          <Route path="gifts" element={<ManageGiftsPage />} />
                          <Route path="levels" element={<LevelsPage />} />
                          <Route path="statistics" element={<StatisticsPage />} />
                          <Route path="dashboard" element={<KidDashboardPage />} />
                          <Route path="friends" element={<FriendsPage />} />
                          <Route path="energy" element={<EnergyPage />} />
                        </Routes>
                      </UserProvider>
                    </ProtectedRoute>
                  } />
                </Routes>
              </LevelProvider>
            </AuthProvider>
          </Router>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;